import React from 'react';
import RavenUtil from '../../../common/ravenUtil';
import Events from '../../../common/events';
import PropTypes from 'prop-types';
import { EVA_TIMELINE_TIMESCALES } from './EnhancedVideoAccess';
import { EVA_TIMELINE_DRAGGER_DEFAULT_DURATION_SECONDS } from './EnhancedVideoAccess';
import { strings } from '../../../../localization';
import { RcAPI, apiRequest, getApiBaseUrl } from '../../../common/rcApi';
import ProgressSpinnerSmall from '../../../common/ProgressSpinnerSmall';
import { requestEventMediaFullscreen } from '../EventMedia/EventMedia';
import EventMediaControl from '../EventMedia/EventMediaControl';
import imageFullscreen from './../EventMedia/images/fullscreen-w64.svg';
import imageCamToggle from './../EventMedia/images/cam-toggle-w64.svg';


const UNIX_TIMESTAMP_COEFFICIENT_FOR_MINUTE_TIMESCALE_DEMARCATIONS = 60 * 5; // demarcation every 5 minutes

const EVA_TIMELINE_CONTAINER_SIDE_MARGIN = 32;
const EVA_TIMELINE_DRAGGER_HANDLE_WIDTH = 24;
const EVA_TIMELINE_DRAGGER_HANDLE_AUTO_SCROLL_DISTANCE = 30;
const EVA_TIMELINE_DEMARCATIONS_WIDTH = 70;

const EVA_ON_DRAGGER_MOVE_THROTTLE_DELAY = 2000;

const EVA_TIMELINE_DRAGGER_DEFAULT_COORDINATE = 0; // pixels = seconds at default time scale

const EVA_TIMELINE_MIN_INDEX_DIFFERENCE = 30; // the minimum difference between start and end route array indexes to check for similar GPS points
const EVA_TIMELINE_MIN_DISTANCE_TOLERANCE = 0.00005; // the minimum distance between two GPS points to decide whether to treat them as one point

const EVA_TIMELINE_REFRESH_SIGNED_URLS_DELAY = 50 * 60 * 1000; // 50 minutes (in milliseconds). Signed URLs expire after 1 hour. Refresh them 10 minutes before expiry.
const EVA_TIMELINE_RETRY_GETTING_REQUESTED_PREVIEWS_DELAY = 5000; // In milliseconds, delay before retrying to get requested (custom and precise) previews when in first GET request previews were not ready
const EVA_TIMELINE_TRY_GETTING_CUSTOM_PREVIEWS_DELAY = 5000; // In milliseconds, delay before trying to get requested custom previews after all POST requests are done
const EVA_TIMELINE_TRY_GETTING_PRECISE_PREVIEWS_DELAY = 3000; // In milliseconds, delay before trying to get requested precise previews after all POST requests are done
const EVA_TIMELINE_MOUSE_PAUSE_BEFORE_REQUESTING_PRECISE_PREVIEW = 3000; // In milliseconds, delay before requesting a precise preview after mouse/slider stops moving
const EVA_TIMELINE_PREVIEWS_HIDE_DELAY = 2000; // In milliseconds, delay before hiding the previews popup when mouse leaves the dragger or previews popup
const EVA_TIMELINE_PREVIEWS_VARIANCE = 30; // In Seconds, If there is a preview within this many seconds of the requested timestamp, don't request a new one

const LOADING_PREVIEWS_ERROR_REASON = {
    FAILED_LOADING_PREVIEWS: "Failed to get previews", // Either tried multiple times to get custom previews but response was empty (Maybe the preview is not ready yet) OR the state.previews is empty (No pre-existing, events or custom previews)
    ERROR_LOADING_PREVIEWS: "Error getting previews", // Something went wrong while getting pre-existing, events and custom previews (Anything that triggered the Catch() block and is not covered by other reasons)
    FAILED_LOADING_PRECISE_PREVIEW: "Failed to get precise preview", // Tried multiple times to get requested precise preview but response was empty (Maybe the preview is not ready yet)
    ERROR_LOADING_PRECISE_PREVIEW: "Error getting precise preview", // Something went wrong while getting precise preview (Anything that triggered the Catch() block and is not covered by other reasons)
    PREVIEW_NOT_AVAILABLE: "422 Unprocessable Content: PREVIEW is unavailable for the specified timestamp"
}

export default class EnhancedVideoAccessTimeline extends React.PureComponent {

    static propTypes = {
        ravenId: PropTypes.string.isRequired,
        cardId: PropTypes.string,
        route: PropTypes.arrayOf(PropTypes.object),
        startGeostamp: PropTypes.object,
        endGeostamp: PropTypes.object,
        cardStartDate: PropTypes.instanceOf(Date),
        cardEndDate: PropTypes.instanceOf(Date),

        timelineDraggerIsDragging: PropTypes.bool.isRequired,
        setTimelineDraggerIsDragging: PropTypes.func.isRequired,

        currentTimescaleId: PropTypes.string.isRequired,
        // in theory, re-render nightmare --> selectedDate: PropTypes.instanceOf(Date).isRequired,
        draggerDurationSeconds: PropTypes.number.isRequired,
        onDraggerMove: PropTypes.func.isRequired
    }

    state = {
        //timelineDraggerLeftCoord: EVA_TIMELINE_DRAGGER_DEFAULT_COORDINATE, // state updates are too costly during drag operation
        draggerDurationSeconds: undefined,
        refreshingPreviewsSignedUrls: false,
        preExistingPreviewsLoading: true,
        preExistingPreviewsLoaded: false,
        eventsPreviewsLoading: false,
        eventsPreviewsLoaded: false,
        customPreviewsLoading: false,
        customPreviewsLoaded: false,
        precisePreviewsLoading: false,
        loadingPreviewsFailed: false,
        loadingPrecisePreviewsFailed: false,
        reasonOfLoadingPreviewsFailed: undefined,
        previews: [],
        waitingPreciseTimestamps: [],
        ignoredPreciseTimestamps: []
    }

    apiAbortController = new AbortController();
    apiQueue = new RcApiQueue(this.props.stage, this.props.ravenId, 5, this.apiAbortController);

    previewsInterval = undefined;
    getRequestedPreviewsTriesLeft = 3;
    previewsDivRef = React.createRef();
    previewRoadImgRef = React.createRef();
    previewCabinImgRef = React.createRef();
    getRequestedPreviewsTimer = undefined;
    requestPrecisePreviewsTimer = undefined;
    refreshPreviewsSignedUrlsTimer = undefined;
    hidePreviewsTimer = undefined;

    onDraggerMoveThrottleTimerId = undefined;

    evaTimelineScrollWrapperContainerRef = React.createRef();
    evaVideoTimelineRef = React.createRef();
    evaVideoTimelineAutoScrollLeftRef = React.createRef();
    evaVideoTimelineAutoScrollRightRef = React.createRef();
    onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = undefined;
    evaVideoTimelineDraggerRef = React.createRef();
    evaVideoTimelineDraggerTooltipRef = React.createRef();
    evaVideoTimelineDraggerTooltipUpdateInterval = undefined;
    timelineDraggingMouseX = undefined;

    evaVideoTimelineDraggerRefLeftCoordinate = EVA_TIMELINE_DRAGGER_DEFAULT_COORDINATE - EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
    evaVideoTimelineDraggerRefDurationSeconds = EVA_TIMELINE_DRAGGER_DEFAULT_DURATION_SECONDS;
    gpsIndicesByUnixTimestampFastLookupCache = undefined;

    getKeyForGpsIndicesByUnixTimestampFastLookupCache = (unixTimestamp) => {

        const fastLookupCandidateKeyNotRounded = unixTimestamp / 100; // do not round this number b/c that reduces accuracy for comparison
        const fastLookupCandidateKey = Math.round(fastLookupCandidateKeyNotRounded);

        if (!this.gpsIndicesByUnixTimestampFastLookupCache) {
            return Math.round(fastLookupCandidateKey);
        }
        if (this.gpsIndicesByUnixTimestampFastLookupCache[fastLookupCandidateKey]) {
            return fastLookupCandidateKey;
        }

        // or attempt a nearest neighbour match against all fastLookup keys
        const availableKeys = Object.keys(this.gpsIndicesByUnixTimestampFastLookupCache);
        let candidateKey; // retrying (see beginning of for-loop below)
        let durationFromCurrentCandidateKey;
        for (let i = 0; i < availableKeys.length; i += 1) {
            if (!candidateKey) {
                candidateKey = availableKeys[i];
                durationFromCurrentCandidateKey = Math.abs(fastLookupCandidateKeyNotRounded - candidateKey);
                continue;
            }

            const durationFromAvailableKey = Math.abs(fastLookupCandidateKeyNotRounded - availableKeys[i]);
            const durationFromNextAvailableKey = i < availableKeys.length - 1 ? Math.abs(fastLookupCandidateKeyNotRounded - availableKeys[i + 1]) : undefined;

            if (durationFromAvailableKey < durationFromCurrentCandidateKey) {
                candidateKey = availableKeys[i];
                durationFromCurrentCandidateKey = Math.abs(fastLookupCandidateKeyNotRounded - candidateKey);
                continue;
            } else if (durationFromNextAvailableKey && durationFromNextAvailableKey < durationFromCurrentCandidateKey) {
                continue; // loop again to set the next availableKey as candidateKey;
            }
            break; // done
        }
        return candidateKey;
    };

    basicDistanceCheckBetweenTwoPoints = (startGeostamp, endGeostamp) => { // I don't care about haversine for this basic-as-possible calc

        if (!startGeostamp || !endGeostamp) return;

        const x1 = startGeostamp.lon;
        const x2 = endGeostamp.lon;
        const y1 = startGeostamp.lat;
        const y2 = startGeostamp.lat;

        // get distances for x's and y's
        let xdistance = x2 - x1;
        let ydistance = y2 - y1;

        return Math.sqrt((xdistance * xdistance) + (ydistance * ydistance));
    }

    syncDataForGpsIndicesByUnixTimestampFastLookupCache = (prevProps) => {

        if (this.props.mapUpdateKey) { // This prop should only be available on Dashboard
            if (this.props.mapUpdateKey === prevProps.mapUpdateKey) { // only reprocess the cache if live update to map detected
                return;
            }
        } else if (this.gpsIndicesByUnixTimestampFastLookupCache) { // history, only cache it once per card
            return;
        }

        const refreshedGpsIndicesByUnixTimestampFastLookupCache = {};

        if (this.props.route) {

            this.props.route.forEach((geoStamp, index) => {

                const geostampTs = geoStamp.ts;
                const geostampDate = RavenUtil.toDate(geostampTs);
                const geostampUnixTs = Math.floor(geostampDate.getTime() / 1000);
                const cachedSegmentLookupKey = this.getKeyForGpsIndicesByUnixTimestampFastLookupCache(geostampUnixTs);

                if (!refreshedGpsIndicesByUnixTimestampFastLookupCache[cachedSegmentLookupKey]) {
                    refreshedGpsIndicesByUnixTimestampFastLookupCache[cachedSegmentLookupKey] = [];
                }

                refreshedGpsIndicesByUnixTimestampFastLookupCache[cachedSegmentLookupKey].push([
                    geostampUnixTs, // unix timestamp to match on drag events
                    index                // corresponding index for map route highlight request
                ]);
            });

            this.gpsIndicesByUnixTimestampFastLookupCache = refreshedGpsIndicesByUnixTimestampFastLookupCache;
            this.requestMapRouteHighlight();
        }
    }

    componentDidMount() {
        this.getPreviewsForTimeline();
        document.addEventListener('fullscreenchange', this.evaVideoTimelinePreviewsPopupFullscreenEscapeHandler);        
        document.addEventListener('click', this.evaVideoTimelinePreviewsPopupBlurHandler);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        if (this.evaVideoTimelineDraggerTooltipRef.current && this.evaVideoTimelineDraggerTooltipRef.current.classList) {
            if (!this.props.timelineDraggerIsDragging) {

                clearInterval(this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval);
                this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = undefined;

                clearInterval(this.evaVideoTimelineDraggerTooltipUpdateInterval);
                this.evaVideoTimelineDraggerTooltipUpdateInterval = undefined;

                this.evaVideoTimelineDraggerTooltipRef.current.classList.remove('right');
                this.evaVideoTimelineDraggerTooltipRef.current.classList.remove('visible');
            } else {

                this.evaVideoTimelineDraggerTooltipRef.current.classList.add('visible');

                if (!this.evaVideoTimelineDraggerTooltipUpdateInterval) {
                    if (this.routeHighlightStartDate) {
                        this.evaVideoTimelineDraggerTooltipRef.current.innerHTML = this.routeHighlightStartDate.toLocaleTimeString();
                    }
                    this.evaVideoTimelineDraggerTooltipUpdateInterval = setInterval(() => {
                        if (this.routeHighlightStartDate) {
                            this.evaVideoTimelineDraggerTooltipRef.current.innerHTML = this.routeHighlightStartDate.toLocaleTimeString();
                        }
                    }, 500);
                }
            }
        }

        this.syncDataForGpsIndicesByUnixTimestampFastLookupCache(prevProps); // including prevProps for comparison only

        let shouldRequestRouteHighlight = false;       

        if (this.evaVideoTimelineDraggerRefDurationSeconds !== this.props.draggerDurationSeconds) {

            if (this.evaVideoTimelineDraggerRef.current) {

                this.evaVideoTimelineDraggerRefDurationSeconds = this.props.draggerDurationSeconds;
                this.evaVideoTimelineDraggerRef.current.style.width = String(this.evaVideoTimelineDraggerRefDurationSeconds + (2 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH)) + "px";

            }
            shouldRequestRouteHighlight = true;
        }


        if (this.props.controlPanelRequestedSelectedDate !== prevProps.controlPanelRequestedSelectedDate) {

            if (this.evaTimelineScrollWrapperContainerRef.current &&
                this.evaVideoTimelineDraggerRef.current) {

                const cardStartUnixTs = Math.floor(this.props.cardStartDate.getTime() / 1000);

                let controlPanelRequestedSelectedDateUnixTs = Math.floor(this.props.controlPanelRequestedSelectedDate.getTime() / 1000);

                if (controlPanelRequestedSelectedDateUnixTs < cardStartUnixTs) {
                    controlPanelRequestedSelectedDateUnixTs = 0;
                }
                const requestedOffsetSeconds = controlPanelRequestedSelectedDateUnixTs - cardStartUnixTs;

                if (this.props.currentTimescaleId === EVA_TIMELINE_TIMESCALES.MINUTE.ID) {
                    const requestedOffsetPixels = requestedOffsetSeconds - EVA_TIMELINE_DRAGGER_HANDLE_WIDTH; // TODO remove this 24 pixel offset stuff
                    this.evaVideoTimelineDraggerRefLeftCoordinate = requestedOffsetPixels;

                    this.evaVideoTimelineDraggerRef.current.style.left = this.evaVideoTimelineDraggerRefLeftCoordinate + "px";                    

                    const evaTimelineScrollWrapperContainerBoundingRect = this.evaTimelineScrollWrapperContainerRef.current.getBoundingClientRect();
                    const evaVideoTimelineDraggerBoundingRect = this.evaVideoTimelineDraggerRef.current.getBoundingClientRect();

                    const newScrollLeft = this.evaVideoTimelineDraggerRefLeftCoordinate - (evaTimelineScrollWrapperContainerBoundingRect.width / 2) + (evaVideoTimelineDraggerBoundingRect.width / 2) + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;// + (evaTimelineScrollWrapperContainerBoundingRect.width / 2);// + (this.props.draggerDurationSeconds));// + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH / 2) );
                    this.evaTimelineScrollWrapperContainerRef.current.scrollLeft = newScrollLeft;

                    // User has clicked on an event in the Events list and the timeline has been scrolled to the event
                    // As this does not include user moving the slider or an autoScroll, the previews popup's position does not update
                    // So we need to move it to the position of the event's timestamp/preview
                    if(this.state.customPreviewsLoaded){
                        // I'm passing the calculated scrollLeft because the 
                        // 'this.evaTimelineScrollWrapperContainerRef.current.scrollLeft' which is used to update popup poistion is not updated yet
                        this.movePreviewPopupToEvent(newScrollLeft);
                    }
                    else{
                        // If loading of previews is not finished yet
                        // Add this to the list of ignored precise timestamps
                        // So that it will be treated like a request for precise preview
                        this.setState({ignoredPreciseTimestamps: [...this.state.ignoredPreciseTimestamps, controlPanelRequestedSelectedDateUnixTs]});
                    }

                } else {
                    console.warn("IMCOMPLETE IMPLEMENTATION - componentDidUpdate controlPanelRequestedSelectedDate change currentTimescale not recognized", this.props.currentTimescaleId);
                    return;
                }

                
            }
            shouldRequestRouteHighlight = true;
        }

        if (shouldRequestRouteHighlight) this.requestMapRouteHighlight();

        // Only after the preExisting and events previews are loaded, request the custom previews (interval-based thumbnails)
        if (this.canRequestCustomPreviews()) {
            this.requestCustomPreviews();
        }

        // As soon as the first preview is loaded, start the timer to refresh the signed URLs before they expire
        if(this.shouldSetRefreshPreviewsSignedUrlsTimer(prevState)){
            this.setRefreshPreviewsSignedUrlsTimer();
        }

    }

    componentWillUnmount() {

        if(this.apiAbortController){
            this.apiAbortController.abort();
        }

        if(this.evaVideoTimelineDraggerRef.current){
            this.evaVideoTimelineDraggerRef.current.removeEventListener('mouseenter', this.evaVideoTimelineDraggerMouseEnterHandler);
            this.evaVideoTimelineDraggerRef.current.removeEventListener('mouseleave', this.evaVideoTimelineDraggerMouseLeaveHandler);
        }

        if(this.previewsDivRef.current){
            this.previewsDivRef.current.removeEventListener('mouseenter', this.evaVideoTimelinePreviewsPopupMouseEnterHandler);
            this.previewsDivRef.current.removeEventListener('mouseleave', this.evaVideoTimelinePreviewsPopupMouseLeaveHandler);
        }

        document.removeEventListener('fullscreenchange', this.evaVideoTimelinePreviewsPopupFullscreenEscapeHandler);
        document.removeEventListener('click', this.evaVideoTimelinePreviewsPopupBlurHandler);

        clearTimeout(this.onDraggerMoveThrottleTimerId);
        this.onDraggerMoveThrottleTimerId = undefined;

        clearTimeout(this.getRequestedPreviewsTimer);
        this.getRequestedPreviewsTimer = undefined;

        clearTimeout(this.requestPrecisePreviewsTimer);
        this.requestPrecisePreviewsTimer = undefined;

        clearTimeout(this.hidePreviewsTimer);
        this.hidePreviewsTimer = undefined;

        clearInterval(this.refreshPreviewsSignedUrlsTimer);
        this.refreshPreviewsSignedUrlsTimer = undefined;
    }

    evaVideoTimelineDraggerMouseEnterHandler = (event) => {
        this.togglePreviews(true);
    }

    evaVideoTimelineDraggerMouseLeaveHandler = (event) => {
        this.togglePreviews(false);
    }

    evaVideoTimelinePreviewsPopupMouseEnterHandler = (event) => {
        this.togglePreviews(true);
    }

    evaVideoTimelinePreviewsPopupMouseLeaveHandler = (event) => {
        this.togglePreviews(false);
    }

    evaVideoTimelinePreviewsPopupFullscreenEscapeHandler = (event) => {
        // Only when th fullscreenchange is caused by user exiting fullscreen by clicking the escape key
        if(!document.fullscreenElement){
            this.togglePreviewsFullscreen(event);
        }
    }

    evaVideoTimelinePreviewsPopupBlurHandler = (event) => {
        const clickedInsideThePreviewsPopup = this.previewsDivRef.current && this.previewsDivRef.current.contains(event.target);
        const clickedInsideTheTimeline = this.evaVideoTimelineRef.current && this.evaVideoTimelineRef.current.contains(event.target);

        if (!clickedInsideThePreviewsPopup && !clickedInsideTheTimeline) {
            this.togglePreviews(false, true);
        }
    }

    minuteTimescaleDemarcationsTimestamps = (cardStartUnixTs, cardEndUnixTs) => {

        let timelinePosition = cardStartUnixTs + UNIX_TIMESTAMP_COEFFICIENT_FOR_MINUTE_TIMESCALE_DEMARCATIONS;

        let demarcationUnixTimestamps = [];

        while (timelinePosition < cardEndUnixTs) {
            demarcationUnixTimestamps.push(Math.floor(timelinePosition / UNIX_TIMESTAMP_COEFFICIENT_FOR_MINUTE_TIMESCALE_DEMARCATIONS) * UNIX_TIMESTAMP_COEFFICIENT_FOR_MINUTE_TIMESCALE_DEMARCATIONS);
            timelinePosition += UNIX_TIMESTAMP_COEFFICIENT_FOR_MINUTE_TIMESCALE_DEMARCATIONS;
        }

        return demarcationUnixTimestamps;
    }

    moveTimelineDraggerToMouseHorizontalPosition = (clientX) => {
        const pointerDistanceFromWindowLeftEdge = clientX;
        const evaTimelineBoundingRect = this.evaVideoTimelineRef.current.getBoundingClientRect();
        const evaVideoTimelineDraggerBoundingRect = this.evaVideoTimelineDraggerRef.current.getBoundingClientRect();        

        let newLeftCoordinate = pointerDistanceFromWindowLeftEdge - evaTimelineBoundingRect.left - (evaVideoTimelineDraggerBoundingRect.width / 2);

        const minLeftCoordinate = -1 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
        const maxLeftCoordinate = evaTimelineBoundingRect.width - evaVideoTimelineDraggerBoundingRect.width + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;

        if (newLeftCoordinate < minLeftCoordinate) {
            newLeftCoordinate = minLeftCoordinate;
        }
        if (newLeftCoordinate > maxLeftCoordinate) {
            newLeftCoordinate = maxLeftCoordinate;
        }

        this.evaVideoTimelineDraggerRefLeftCoordinate = newLeftCoordinate;

        this.evaVideoTimelineDraggerRef.current.style.left = this.evaVideoTimelineDraggerRefLeftCoordinate + "px";
        
        this.requestMapRouteHighlight();
        this.updatePreviewPopup();
    }

    onEnhancedVideoAccessPanelMouseMove = (event) => {
        if (this.props.timelineDraggerIsDragging) {

            this.handleRequestForPrecisePreview();
            
            this.timelineDraggingMouseX = event.clientX; // update reference used by either auto-scroll and/or basic dragger position update

            // First, check if auto-scroll necessary
            const evaTimelineScrollWrapperContainerBoundingRect = this.evaTimelineScrollWrapperContainerRef.current.getBoundingClientRect(); // for the width and timeline frame
            const evaVideoTimelineAutoScrollLeftRefBoundingRect = this.evaVideoTimelineAutoScrollLeftRef.current.getBoundingClientRect();
            const evaVideoTimelineAutoScrollRightRefBoundingRect = this.evaVideoTimelineAutoScrollRightRef.current.getBoundingClientRect()
            const evaVideoTimelineDraggerBoundingRect = this.evaVideoTimelineDraggerRef.current.getBoundingClientRect();

            const approximateEvaTimelineScrollWrapperContainerMiddleCoordinate = evaTimelineScrollWrapperContainerBoundingRect.left + (evaTimelineScrollWrapperContainerBoundingRect.width / 2); // approxime because not bothering with margin offset (will be 16 pixels off at time of writing)
            const evaDraggerMiddleCoordinate = this.timelineDraggingMouseX + (evaVideoTimelineDraggerBoundingRect.width / 2);

            if (evaDraggerMiddleCoordinate <= approximateEvaTimelineScrollWrapperContainerMiddleCoordinate) {

                if (this.evaVideoTimelineDraggerRef.current.style.width !== this.evaVideoTimelineAutoScrollLeftRef.current.style.width) {
                    this.evaVideoTimelineAutoScrollLeftRef.current.style.width = this.evaVideoTimelineDraggerRef.current.style.width; 
                }
                if (evaVideoTimelineAutoScrollLeftRefBoundingRect.right > this.timelineDraggingMouseX) { // mouse pointer has past left edge guide's threshold

                    if (!this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval) {
                        this.evaTimelineScrollWrapperContainerRef.current.style.scrollBehavior = "auto"; // browser easing on scroll position updates causes huge inaccuracies (dragger will fly off view)
                        this.onEnhancedVideoAccessTimnelineAutoScrollLeftMouseHover();
                        this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = setInterval(this.onEnhancedVideoAccessTimnelineAutoScrollLeftMouseHover, 100);
                    }

                } else {

                    clearInterval(this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval);
                    this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = undefined;

                    this.evaTimelineScrollWrapperContainerRef.current.style.scrollBehavior = "smooth"; // reinstate browser easing
                }

            } else {

                if (this.evaVideoTimelineDraggerRef.current.style.width !== this.evaVideoTimelineAutoScrollRightRef.current.style.width) {
                    this.evaVideoTimelineAutoScrollRightRef.current.style.width = this.evaVideoTimelineDraggerRef.current.style.width; 
                }

                if (evaVideoTimelineAutoScrollRightRefBoundingRect.left < this.timelineDraggingMouseX) { // mouse pointer has past right edge guide's threshold
    
                    if (!this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval) {
                        this.evaTimelineScrollWrapperContainerRef.current.style.scrollBehavior = "auto"; // browser easing on scroll position updates causes huge inaccuracies (dragger will fly off view)
                        this.evaVideoTimelineDraggerTooltipRef.current.classList.add('right');
                        this.onEnhancedVideoAccessTimnelineAutoScrollRightMouseHover();
                        this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = setInterval(this.onEnhancedVideoAccessTimnelineAutoScrollRightMouseHover, 100);
                    }

                } else {

                    clearInterval(this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval);
                    this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval = undefined;
                    this.evaVideoTimelineDraggerTooltipRef.current.classList.remove('right');
                    this.evaTimelineScrollWrapperContainerRef.current.style.scrollBehavior = "smooth"; // reinstate browser easing
                }
            }
            
            // Lastly, if auto-scroll is not active, do a basic dragger position update
            if (!this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval) { // (when auto-scroll interval present, it handles mouse movements instead)
                this.moveTimelineDraggerToMouseHorizontalPosition(this.timelineDraggingMouseX);
            }
        }
    }

    enhancedVideoAccessTimnelineAutoScroll = (scrollForward) => {
        let scrollDisplacement = EVA_TIMELINE_DRAGGER_HANDLE_AUTO_SCROLL_DISTANCE;
        if (!scrollForward) {
            scrollDisplacement = -1 * scrollDisplacement;
        }
        const previousScrollPosition = this.evaTimelineScrollWrapperContainerRef.current.scrollLeft;
        this.evaTimelineScrollWrapperContainerRef.current.scrollLeft += scrollDisplacement; // shift the timeline
        const currentScrollPosition = this.evaTimelineScrollWrapperContainerRef.current.scrollLeft;

        if (previousScrollPosition - currentScrollPosition === 0) { // CSS-enforced min or max scroll position reached (auto-scroll complete)
            // mimick a basic dragger position update
            this.moveTimelineDraggerToMouseHorizontalPosition(this.timelineDraggingMouseX);
            return;
        }

        const evaTimelineBoundingRect = this.evaVideoTimelineRef.current.getBoundingClientRect();
        const evaVideoTimelineDraggerBoundingRect = this.evaVideoTimelineDraggerRef.current.getBoundingClientRect();

        let newLeftCoordinate = this.evaVideoTimelineDraggerRefLeftCoordinate + scrollDisplacement; // offset the current dragger position as per timeline shift

        const minLeftCoordinate = -1 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
        const maxLeftCoordinate = evaTimelineBoundingRect.width - evaVideoTimelineDraggerBoundingRect.width + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;

        if (newLeftCoordinate < minLeftCoordinate) {
            newLeftCoordinate = minLeftCoordinate;
        }
        if (newLeftCoordinate > maxLeftCoordinate) {
            newLeftCoordinate = maxLeftCoordinate;
        }

        this.evaVideoTimelineDraggerRefLeftCoordinate = newLeftCoordinate;

        this.evaVideoTimelineDraggerRef.current.style.left = this.evaVideoTimelineDraggerRefLeftCoordinate + "px";
        //this.evaVideoTimelineDraggerRef.current.style.width = String(this.evaVideoTimelineDraggerRefDurationSeconds + (2 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH)) + "px";

        this.requestMapRouteHighlight();
        this.updatePreviewPopup();

    }

    onEnhancedVideoAccessTimnelineAutoScrollLeftMouseHover = () => {
        this.enhancedVideoAccessTimnelineAutoScroll(false);
    }

    onEnhancedVideoAccessTimnelineAutoScrollRightMouseHover = () => {
        this.enhancedVideoAccessTimnelineAutoScroll(true);
    }

    onTimelineBarClick = (event) => {
        if (event.target !== event.currentTarget) {
            return;
        }
        if (!this.props.timelineDraggerIsDragging &&
            this.evaTimelineScrollWrapperContainerRef.current &&
            this.evaVideoTimelineRef.current &&
            this.evaVideoTimelineDraggerRef.current) {
                
            this.timelineDraggingMouseX = event.clientX;

            this.moveTimelineDraggerToMouseHorizontalPosition(this.timelineDraggingMouseX);

            this.handleRequestForPrecisePreview();
        }
    }

    throttleFunction = (func, delay) => {

        if (this.onDraggerMoveThrottleTimerId) {
            return;
        }
    
        this.onDraggerMoveThrottleTimerId = setTimeout( () => {
            func()
            this.onDraggerMoveThrottleTimerId = undefined;
        }, delay)
    }

    throttledOnDraggerMoveUpdate = () => {
        this.throttleFunction(() => {
            this.props.onDraggerMove(this.routeHighlightStartDate);
        }, EVA_ON_DRAGGER_MOVE_THROTTLE_DELAY);
    }
    
    requestMapRouteHighlight = () => {

        if (!this.evaVideoTimelineRef.current || !this.evaVideoTimelineDraggerRef.current) {
            console.warn("EnhancedVideoAccess required refs not ready yet.");
            return;
        }

        const evaTimelineBoundingRect = this.evaVideoTimelineRef.current.getBoundingClientRect();
        const evaVideoTimelineDraggerBoundingRect = this.evaVideoTimelineDraggerRef.current.getBoundingClientRect();

        let evaDraggerLeftOffsetRelativeToTimelineLeftEdge = Math.floor(evaVideoTimelineDraggerBoundingRect.left - evaTimelineBoundingRect.left);
        if (evaDraggerLeftOffsetRelativeToTimelineLeftEdge < 0) {
            evaDraggerLeftOffsetRelativeToTimelineLeftEdge = 0;
        }

        let evaDraggerRightOffsetRelativeToTimelineLeftEdge = Math.floor(evaVideoTimelineDraggerBoundingRect.right - evaTimelineBoundingRect.left);
        if (evaDraggerRightOffsetRelativeToTimelineLeftEdge < 0) {
            evaDraggerRightOffsetRelativeToTimelineLeftEdge = 0;
        }

        if (!this.props.route) {
            console.warn("EnhancedVideoAccess requestMapRouteHighlight route prop not found.");
            return;
        }

        if (!this.props.cardStartDate) {
            console.warn("EnhancedVideoAccess requestMapRouteHighlight cardStartDate prop not found.");
            return;
        }

        const cardStartUnixTs = Math.floor(this.props.cardStartDate.getTime() / 1000);

        // get dragger coordinates at 1 pixel = 1 second default timescale
        let routeHighlightStartUnixTs = cardStartUnixTs; // initiate each coord at the 0-pixel position (start time)
        let routeHighlightEndUnixTs = cardStartUnixTs;

        if (this.props.currentTimescaleId === EVA_TIMELINE_TIMESCALES.MINUTE.ID) {
            routeHighlightStartUnixTs += evaDraggerLeftOffsetRelativeToTimelineLeftEdge + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH; // TODO remove this 24 pixel offset stuff
            routeHighlightEndUnixTs += evaDraggerRightOffsetRelativeToTimelineLeftEdge - EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
        } else {
            console.warn("IMCOMPLETE IMPLEMENTATION - requestMapRouteHighlight state currentTimescale not recognized", this.props.currentTimescaleId);
            return;
        }

        this.routeHighlightStartDate = new Date(routeHighlightStartUnixTs * 1000);
        this.throttledOnDraggerMoveUpdate();

        if (!this.gpsIndicesByUnixTimestampFastLookupCache) {
            console.warn("EnhancedVideoAccess requestMapRouteHighlight route lookup cache not found.");
            return;
        }

        const startTsCachedSegmentLookupKey = this.getKeyForGpsIndicesByUnixTimestampFastLookupCache(routeHighlightStartUnixTs);
        const startTsCachedSegment = this.gpsIndicesByUnixTimestampFastLookupCache[startTsCachedSegmentLookupKey];
        let routeHighlightStartIndex;


        if (startTsCachedSegment) {

            let candidate;
            for (let i = 0; i < startTsCachedSegment.length; i += 1) {
                if (!candidate) {
                    candidate = startTsCachedSegment[i];
                    routeHighlightStartIndex = candidate[1];
                    continue;
                }
                const diffFromCandidate = Math.abs(routeHighlightStartUnixTs - candidate[0]);
                const diffFromCurrent = Math.abs(routeHighlightStartUnixTs - startTsCachedSegment[i][0]);
                if (diffFromCurrent < diffFromCandidate) {
                    candidate = startTsCachedSegment[i];
                    routeHighlightStartIndex = candidate[1];
                    continue;
                }
                break;
            }
        } else { // Not enough GPS points to request a reasonably accurate highlight starting position
            //console.warn("EnhancedVideoAccess requestMapRouteHighlight startTsCachedSegment not found");
        }

        const endTsCachedSegmentLookupKey = this.getKeyForGpsIndicesByUnixTimestampFastLookupCache(routeHighlightEndUnixTs);
        const endTsCachedSegment = this.gpsIndicesByUnixTimestampFastLookupCache[endTsCachedSegmentLookupKey];
        let routeHighlightEndIndex;

        if (endTsCachedSegment) {

            let candidate;
            for (let i = 0; i < endTsCachedSegment.length; i += 1) {
                if (!candidate) {
                    candidate = endTsCachedSegment[i];
                    routeHighlightEndIndex = candidate[1];
                    continue;
                }
                const diffFromCandidate = Math.abs(routeHighlightEndUnixTs - candidate[0]);
                const diffFromCurrent = Math.abs(routeHighlightEndUnixTs - endTsCachedSegment[i][0]);
                if (diffFromCurrent < diffFromCandidate) {
                    candidate = endTsCachedSegment[i];
                    routeHighlightEndIndex = candidate[1];
                    continue;
                }
                break;
            }
        } else { // Not enough GPS points to request a reasonably accurate highlight ending position
            //console.warn("EnhancedVideoAccess requestMapRouteHighlight endTsCachedSegment not found");
        }

        if (routeHighlightStartIndex && routeHighlightEndIndex) {

            // check for edge-case where the start and end indexes, though different, end up pointing to the same gps values
            if (Math.abs(routeHighlightEndIndex - routeHighlightStartIndex) <= EVA_TIMELINE_MIN_INDEX_DIFFERENCE) {
                const basicDistance = this.basicDistanceCheckBetweenTwoPoints(this.props.route[routeHighlightStartIndex], this.props.route[routeHighlightEndIndex]);

                if (basicDistance < EVA_TIMELINE_MIN_DISTANCE_TOLERANCE) {
                /*if ((this.props.route[routeHighlightStartIndex].lat === this.props.route[routeHighlightEndIndex].lat) &&
                    (this.props.route[routeHighlightStartIndex].lon === this.props.route[routeHighlightEndIndex].lon)) {*/

                    Events.enhancedVideoAccessRouteHighlightRequest(this.props.ravenId, routeHighlightStartIndex, routeHighlightStartIndex); // send the same index so it displayable on the map as a point
                    return;
                }

            }

            Events.enhancedVideoAccessRouteHighlightRequest(this.props.ravenId, routeHighlightStartIndex, routeHighlightEndIndex);
            return;
        } else if (routeHighlightStartIndex && !routeHighlightEndIndex) {

            Events.enhancedVideoAccessRouteHighlightRequest(this.props.ravenId, routeHighlightStartIndex, routeHighlightStartIndex);
            return; // redundant but including it here and elsewhere just in case this function gets more complex
        } else if (routeHighlightEndIndex && !routeHighlightStartIndex) {

            Events.enhancedVideoAccessRouteHighlightRequest(this.props.ravenId, routeHighlightEndIndex, routeHighlightEndIndex);
            return;
        } else {

            Events.enhancedVideoAccessRouteHighlightRequest(this.props.ravenId, undefined, undefined); // <-- clear any current highlight
            return;
        }
    }

    onScrollButtonClick = (scrollForward) => {
        if (!this.evaTimelineScrollWrapperContainerRef.current) {
            console.warn("EnhancedVideoAccess onScrollButtonClick missing evaTimelineScrollWrapperContainerRef");
            return;
        }
        const evaTimelineScrollWrapperContainerBoundingRect = this.evaTimelineScrollWrapperContainerRef.current.getBoundingClientRect();
        const scrollAmount = (evaTimelineScrollWrapperContainerBoundingRect.width * 0.50);
        if (scrollForward) {
            this.evaTimelineScrollWrapperContainerRef.current.scrollBy(scrollAmount, 0);
            return;
        }
        this.evaTimelineScrollWrapperContainerRef.current.scrollBy(-scrollAmount, 0);
    }

    getPreviewsForTimeline = () => {
        this.previewsInterval = this.calculatePreviewsInterval();
        this.getPreExistingPreviews();
        this.getEventsPreviews();
    }

    getPreExistingPreviews = (nextPageUrl) => {

        let url = nextPageUrl;

        if(!url){
            const start_timestamp = this.props.cardStartDate.getTime() / 1000;
            const end_timestamp = this.props.cardEndDate.getTime() / 1000;
            url =`type=preview&signed_urls=true&start_timestamp=${start_timestamp}&end_timestamp=${end_timestamp}`;
            
            // First round of getting preExisting Previews, so preExistingPreviewsLoading should be set to true
            this.setState({preExistingPreviewsLoading: true});
        }

        this.apiQueue.addGetRequest(url)
        .then(r => {
            let currentPreviews = JSON.parse(JSON.stringify(this.state.previews));

            r.results.forEach((preview, i) => {

                let currentPreview = this.requestedPreviewAlreadyExistsIn(currentPreviews, preview.timestamp);

                if(currentPreview){
                    currentPreview[preview.camera.toLowerCase()] = preview.contentUri;
                }
                else{
                    currentPreview =  this.requestedPreviewAlreadyExistsIn(currentPreviews, preview.timestamp, 1);
                    if(currentPreview && currentPreview[preview.camera.toLowerCase()] === undefined){
                        currentPreview[preview.camera.toLowerCase()] = preview.contentUri;
                    }
                    else{
                        currentPreviews.push({
                            [preview.camera.toLowerCase()]: preview.contentUri,
                            ts: preview.timestamp
                        });
                    }
                }
            });

            this.setState({
                previews: this.adjustPreviews(currentPreviews),
            });

            this.updatePreviewPopup(null, true);

            if(r.next){
                this.getPreExistingPreviews(r.next.split('?')[1]);
            }
            else{
                this.setState({
                    preExistingPreviewsLoaded: true, 
                    preExistingPreviewsLoading: false
                });

                // If it's refreshing step: 
                // As pre-existing previews are loaded, if events previews are also loaded, refreshing is done
                if(this.state.refreshingPreviewsSignedUrls && this.state.eventsPreviewsLoaded){
                    this.setState({
                        refreshingPreviewsSignedUrls: false
                    });
                }
            }
        })
        .catch(error => {

            if (error.name === 'AbortError') {
                return;
            }

            console.error("getPreExistingPreviews: Error when loading pre-existing previews ", error);

            this.setState({
                preExistingPreviewsLoaded: true, // Even if there is an error, we should set this to true, as we're done with this and should be able to proceed with other previews
                preExistingPreviewsLoading: false,
                refreshingPreviewsSignedUrls: false,
                loadingPreviewsFailed: true,
                reasonOfLoadingPreviewsFailed: error.code === 422 ? LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE : LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PREVIEWS
            });
        });
    }

    getEventsPreviews = (nextPageUrl) => {

        let url = nextPageUrl;

        if(!url){

            // As events can have same timestamp, we need to get unique timestamps
            let timestamps = new Set();        

            this.props.events.map(e => Math.floor(e.date.getTime() / 1000)).forEach(t => timestamps.add(t));

            if(timestamps.size === 0){
                return;
            }

            const ts = [...timestamps].join(",");

            url = `type=preview&signed_urls=true&ts=${ts}`;

            // First round of getting events Previews, so eventsPreviewsLoading should be set to true
            this.setState({eventsPreviewsLoading: true});
        }

        this.apiQueue.addGetRequest(url)
        .then(r => {
            let currentPreviews = JSON.parse(JSON.stringify(this.state.previews));

            r.results.forEach((preview, i) => {

                const currentPreview = this.requestedPreviewAlreadyExistsIn(currentPreviews, preview.ts);

                const event = this.props.events.find(e => Math.floor(e.date.getTime() / 1000) === preview.ts);

                if(currentPreview){
                    if(event){
                        currentPreview.type = event.type;
                    }
                    
                    if(this.state.refreshingPreviewsSignedUrls){

                        const roadPreview = preview.results.find(p => p.camera === "ROAD");
                        const cabinPreview = preview.results.find(p => p.camera === "CABIN");

                        currentPreview.road = roadPreview ? roadPreview.contentUri : null;
                        currentPreview.cabin = cabinPreview ? cabinPreview.contentUri : null;
                    } 
                    return;
                }
                else{
                    const roadPreview = preview.results.find(p => p.camera === "ROAD");
                    const cabinPreview = preview.results.find(p => p.camera === "CABIN");

                    if((!roadPreview && !cabinPreview)){
                        return;
                    }

                    currentPreviews.push({
                        road: roadPreview ? roadPreview.contentUri : null,
                        cabin: cabinPreview ? cabinPreview.contentUri : null,
                        ts: preview.ts,
                        type: event ? event.type : null,
                    });
                }
            });

            this.setState({
                previews: this.adjustPreviews(currentPreviews),
            });

            this.updatePreviewPopup(null, true);

            if(r.next){
                this.getEventsPreviews(r.next.split('?')[1]);
            }
            else{
                this.setState({
                    eventsPreviewsLoaded: true,
                    eventsPreviewsLoading: false
                });

                // If it's refreshing step: 
                // As events previews are loaded, if pre-existing previews are also loaded, refreshing is done
                if(this.state.refreshingPreviewsSignedUrls && this.state.preExistingPreviewsLoaded){
                    this.setState({
                        refreshingPreviewsSignedUrls: false
                    });
                }
            }            
        })
        .catch(error => {
            
            if (error.name === 'AbortError') {
                return;
            }

            console.error("getEventsPreviews: Error when loading events previews ", error);

            this.setState({
                eventsPreviewsLoaded: true, // Even if there is an error, we should set this to true, as we're done with this and should be able to proceed with other previews
                eventsPreviewsLoading: false,
                loadingPreviewsFailed: true,
                refreshingPreviewsSignedUrls: false,
                reasonOfLoadingPreviewsFailed: error.code === 422 ? LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE : LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PREVIEWS
            });
        });
    }

    getRequestedPreviews = (timestamps, successCallback, errorCallback) => {
        
        const ts = timestamps.join(",");

        const emptyPreviews = [];
        const newPreviews = [];
        const canTryAgain = this.getRequestedPreviewsTriesLeft > 0;

        this.apiQueue.addGetRequest(`type=preview&signed_urls=true&ts=${ts}`)
        .then(r => 
            {
                r.results.forEach((preview, i) => {

                    // This is a second check. Cuz before making the POST request, we checked if the preview already exists in state.previews
                    let previewAlreadyExists = this.requestedPreviewAlreadyExistsIn(this.state.previews, preview.ts);

                    if(previewAlreadyExists){
                        return;
                    }
                    else{
                        const roadPreview = preview.results.find(p => p.camera === "ROAD");
                        const cabinPreview = preview.results.find(p => p.camera === "CABIN");

                        if((!roadPreview || !cabinPreview)){
                            if(canTryAgain)
                                emptyPreviews.push(preview.ts);

                            return;
                        }

                        newPreviews.push({
                            road: roadPreview ? roadPreview.contentUri : null,
                            cabin: cabinPreview ? cabinPreview.contentUri : null,
                            ts: preview.ts
                        });
                    }
                });
                
                successCallback(newPreviews, emptyPreviews);

                this.updatePreviewPopup(null, true);

                if(emptyPreviews.length > 0){
                    this.getRequestedPreviewsTriesLeft--;

                    clearTimeout(this.getRequestedPreviewsTimer);

                    this.getRequestedPreviewsTimer = setTimeout(() => {
                        this.getRequestedPreviews(emptyPreviews, successCallback, errorCallback);
                    }, EVA_TIMELINE_RETRY_GETTING_REQUESTED_PREVIEWS_DELAY);
                }
            }
        )
        .catch(error => {
            
            if (error.name === 'AbortError') {
                return;
            }

            console.error("getRequestedPreviews: Error when loading requested previews ", error);
            if(errorCallback){
                errorCallback(error);
            }
        });
    }

    canRequestCustomPreviews = () => {
        // If there has been an error when loading previews (pex-existing, events or other custom previews), don't request custom previews
        if(this.state.loadingPreviewsFailed){
            return false;
        }

        // If pre-existing or events previews are not loaded yet, don't request custom previews
        if(!this.state.preExistingPreviewsLoaded ||
            !this.state.eventsPreviewsLoaded){
            return false;
        }

        // If custom previews are already loaded or are being loading, don't request custom previews again
        if(this.state.customPreviewsLoaded ||
            this.state.customPreviewsLoading
        ){
            return false;
        }

        return true;
    }

    requestCustomPreviews = () => {

        const start_timestamp = this.props.cardStartDate.getTime() / 1000;
        const end_timestamp = this.props.cardEndDate.getTime() / 1000;

        const timestamps = [];

        // include the start timestamp as one of the timestamps
        let ts = start_timestamp;

        while(ts <= end_timestamp){
            if(this.requestedPreviewAlreadyExistsIn(this.state.previews, ts, EVA_TIMELINE_PREVIEWS_VARIANCE)){
                ts += this.previewsInterval;
                continue;
            }

            timestamps.push(ts);
            ts += this.previewsInterval;
        }

        if(timestamps.length === 0){
            this.setCustomPreviews();
            return;
        }

        this.setState({customPreviewsLoading: true});

        this.apiQueue.callback = () => {
            clearTimeout(this.getRequestedPreviewsTimer);

            this.getRequestedPreviewsTimer = setTimeout(() => {
                this.getRequestedPreviews(timestamps, this.setCustomPreviews, this.customPreviewsError);
            }, EVA_TIMELINE_TRY_GETTING_CUSTOM_PREVIEWS_DELAY);
        }

        timestamps.forEach((ts, i) => {

            const payloadForRoadCamera = {
                timestamp: ts,
                cameraType: "ROAD",
                startOffset: 0,
                duration: 0,
                mediaType: "PREVIEW",
                description: 'Road Preview For ' + ts
            };

            const payloadForCabinCamera = {
                ...payloadForRoadCamera,
                cameraType: "CABIN",
            }

            this.apiQueue.addPostRequest(payloadForRoadCamera).catch(this.customPreviewsError);
            
            this.apiQueue.addPostRequest(payloadForCabinCamera).catch(this.customPreviewsError);
        });
    }

    retryRequestPreviews = () => {

        this.setState({
            preExistingPreviewsLoading: true,
            preExistingPreviewsLoaded: false,
            eventsPreviewsLoading: false,
            eventsPreviewsLoaded: false,
            customPreviewsLoading: false,
            customPreviewsLoaded: false,
            precisePreviewsLoading: false,
            loadingPreviewsFailed: false,
            loadingPrecisePreviewsFailed: false,
            reasonOfLoadingPreviewsFailed: undefined,
            previews: [],
            waitingPreciseTimestamps: [],
            ignoredPreciseTimestamps: []
        });

        this.getPreviewsForTimeline();
    }

    requestPrecisePreviews = (timestamps) => {
        timestamps.forEach((ts, i) => {
            this.requestPrecisePreview(ts);
        });
    }

    requestPrecisePreview = (timestamp) => {

        const end_timestamp = this.props.cardEndDate.getTime() / 1000;

        // If slider is dragged past the end of the trip, don't request previews (This can happen for short trips)
        if(timestamp > end_timestamp){
            return;
        }

        if(this.isRequestedPreviewInParkedSegment(timestamp)){
            this.precisePreviewsError({code: 422});
            return;
        }

        const alreadyExistingPreview = this.requestedPreviewAlreadyExistsIn(this.state.previews, timestamp);

        if(alreadyExistingPreview){
            return;
        }
        
        let timestamps = [timestamp];

        this.setState({
            precisePreviewsLoading: true,
            waitingPreciseTimestamps: [...this.state.waitingPreciseTimestamps, timestamp]
        });

        this.getRequestedPreviewsTriesLeft = 3;

        this.apiQueue.callback = () => {

            clearTimeout(this.getRequestedPreviewsTimer);

            this.getRequestedPreviewsTimer = setTimeout(() => {
                this.getRequestedPreviews(this.state.waitingPreciseTimestamps, this.setPrecisePreviews, this.precisePreviewsError);
            }, EVA_TIMELINE_TRY_GETTING_PRECISE_PREVIEWS_DELAY);
        }

        timestamps.forEach((ts, i) => {

            const payloadForRoadCamera = {
                timestamp: ts,
                cameraType: "ROAD",
                startOffset: 0,
                duration: 0,
                mediaType: "PREVIEW",
                description: 'Road Preview For ' + ts
            }

            const payloadForCabinCamera = {
                ...payloadForRoadCamera,
                cameraType: "CABIN",
            }

            this.apiQueue.addPostRequest(payloadForRoadCamera).catch(this.precisePreviewsError);
            
            this.apiQueue.addPostRequest(payloadForCabinCamera).catch(this.precisePreviewsError);
        });
    }

    retryRequestPrecisePreviews = () => {
        const waitingPreciseTimestamps = [...this.state.waitingPreciseTimestamps];

        this.clearErrorMessages();

        this.requestPrecisePreviews(waitingPreciseTimestamps);
    }

    // RAV-5648 & RAV-5663 : Show a `Not found` message without a 'Retry' button when the timestamp is in a parked segment 
    // The check is done by looking at the last event before the timestamp, if it's an engine off event, then it's a parked segment
    isRequestedPreviewInParkedSegment = (timestamp) => {

        const events = [...this.props.events];

        events.sort((a, b) => a.date.getTime() - b.date.getTime());

        // Filter events before the timestamp
        const eventsBefore = events.filter(event => new Date(event.date).getTime() / 1000 < timestamp);    

        const lastBefore = eventsBefore.length > 0 ? eventsBefore[eventsBefore.length - 1] : null;

        if(!lastBefore){
            return false;
        }

        return lastBefore.type === "EVENT_ENGINE_OFF";
        
    }

    handleRequestForPrecisePreview = () => {
        
        // RAV-5651: If previous request for a preview have failed, and we have shown a error message
        // now that the user has moved the mouse, clear the error message
        this.clearErrorMessages();

        // User has moved the mouse, so reset the timer
        if(this.requestPrecisePreviewsTimer){
            clearTimeout(this.requestPrecisePreviewsTimer);
        }

        const t = this.routeHighlightStartDate.getTime() / 1000;

        this.requestPrecisePreviewsTimer = setTimeout(() => {

            // If the mouse is stationary not because of user's pause but because the autoScroll is happening, don't request the precise preview
            if(this.onEnhancedVideoAccessTimnelineAutoScrollMouseHoverInterval){
                return;
            }
            
            // If other previews are still being loaded, wait for them to load first
            // So we keep track of the timestamps where user has paused the mouse to request the precise preview later
            if(!this.state.customPreviewsLoaded){
                this.setState({
                    ignoredPreciseTimestamps: [...this.state.ignoredPreciseTimestamps, t]
                });
                return;
            }
            
            // Else we can request the precise preview
            this.requestPrecisePreview(t);

        }, EVA_TIMELINE_MOUSE_PAUSE_BEFORE_REQUESTING_PRECISE_PREVIEW);
    }

    calculatePreviewsInterval = () => {
        const tripDuration = (this.props.cardEndDate.getTime() - this.props.cardStartDate.getTime()) / 1000;
        let interval = Math.floor(tripDuration / 19); // To have 20 interval-based previews, we need 19 intervals not 20
        
        // const visibleTimeline = this.evaTimelineScrollWrapperContainerRef.current.clientWidth - (EVA_TIMELINE_DRAGGER_HANDLE_WIDTH * 2);
        // const minInterval = Math.floor(visibleTimeline / 4);

        // if(interval < minInterval){
        //     interval = minInterval;
        // }

        return interval;
    }

    setCustomPreviews = (newPreviews = [], emptyPreviews = []) => {        
        const isDone = emptyPreviews.length === 0;
        
        // If emptyPreviews is empty && newPreviews is empty as well (meaning it has failed getting all or some of the preview(s) in the 3 tries, and there are no more GET requests left),
        const failedToLoad = emptyPreviews.length === 0 && newPreviews.length === 0;

        const previews = this.adjustPreviews(this.state.previews.concat(newPreviews));

        this.setState({
            previews: previews,
            customPreviewsLoading: !isDone,
            customPreviewsLoaded: isDone,
            loadingPreviewsFailed: failedToLoad,
        });

        if(isDone){
            // timestamps where user has paused the mouse and requested a precise preview
            // But we ignored them because we were waiting for the other previews (pre-existing, events and custom previews) to load
            const ignoredPreciseTimestamps = this.state.ignoredPreciseTimestamps;

            // Now request for waiting precise previews
            if(ignoredPreciseTimestamps.length > 0){                
                this.setState({
                    ignoredPreciseTimestamps: []
                });

                this.requestPrecisePreviews(ignoredPreciseTimestamps);
            }
        }
        
    }

    customPreviewsError = (error) => {
        if (error.name === 'AbortError') {
            return;
        }

        console.error("Error when loading custom previews ", error);
        clearTimeout(this.getRequestedPreviewsTimer);
        this.setState({
            customPreviewsLoaded: true, // Even if there is an error, we should set this to true, as we're done with this and should be able to proceed with other previews
            customPreviewsLoading: false,
            loadingPreviewsFailed: true,
            reasonOfLoadingPreviewsFailed: error.code === 422 ? LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE : LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PREVIEWS
        });
    }

    setPrecisePreviews = (newPreviews = [], emptyPreviews = []) => {
        const isDone = emptyPreviews.length === 0;

        const previews = this.adjustPreviews(this.state.previews.concat(newPreviews));
        
        // If emptyPreviews is empty && newPreviews is empty as well (meaning it has failed getting the requested preview(s) in the 3 tries, and there are no more GET requests left), 
        // That means there are still timestamps left in the state.waitingPreciseTimestamps, so clear them
        // Not doing this causes the user to keep seeing the spinner(s)
        // Else, just update the state.waitingPreciseTimestamps and remove from it the timestamps that were successfully requested
        const failedToLoad = emptyPreviews.length === 0 && newPreviews.length === 0;

        const waitingPreciseTimestamps = this.state.waitingPreciseTimestamps.filter(ts => newPreviews.findIndex(p => p.ts === ts) < 0);

        this.setState({
            previews: previews,
            waitingPreciseTimestamps: waitingPreciseTimestamps,
            precisePreviewsLoading: !isDone,
            precisePreviewsLoaded: isDone,
            loadingPrecisePreviewsFailed: failedToLoad, // Show error message if failed to load precise previews in 3 tries
            reasonOfLoadingPreviewsFailed: failedToLoad ? LOADING_PREVIEWS_ERROR_REASON.FAILED_LOADING_PRECISE_PREVIEW : undefined
        });

        if(newPreviews.length > 0){
            // RAV-6076 : If the preview is loaded when user has opened the Request Downalod Modal, don't bring up the previews popup
            const keepHidden = this.props.isRequestDownloadModalOpen;
            this.updatePreviewPopup(newPreviews[0], keepHidden);
        }
    }

    precisePreviewsError = (error) => {

        if (error.name === 'AbortError') {
            return;
        }

        console.error("precisePreviewsError: Error when loading precise previews ", error);
        clearTimeout(this.getRequestedPreviewsTimer);
        this.setState({
            precisePreviewsLoading: false,
            loadingPrecisePreviewsFailed: true,
            reasonOfLoadingPreviewsFailed: error.code === 422 ? LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE : LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PRECISE_PREVIEW,
        });
    }

    getPreviewForInterval = () => {
        if(this.state.previews.length === 0){
            return null;
        }

        const currentTimestamp = this.routeHighlightStartDate ? (this.routeHighlightStartDate.getTime() / 1000) : null;

        if(!currentTimestamp){
            return null;
        }

        const visibleTimelineStartTimestamp = (this.props.cardStartDate.getTime() / 1000)  + this.evaTimelineScrollWrapperContainerRef.current.scrollLeft;
        const visibleTimelineEndTimestamp = visibleTimelineStartTimestamp + this.evaTimelineScrollWrapperContainerRef.current.clientWidth;

        const visiblePreviews = this.state.previews.filter(p => p.ts >= visibleTimelineStartTimestamp && p.ts <= visibleTimelineEndTimestamp);

        const indexOfPreviewOnTheLeft = visiblePreviews.findIndex(p => p.ts <= currentTimestamp && p.ts >= visibleTimelineStartTimestamp);

        const previewOnTheLeft = visiblePreviews[indexOfPreviewOnTheLeft];
        const previewOnTheRight = previewOnTheLeft ? visiblePreviews[indexOfPreviewOnTheLeft - 1] : visiblePreviews[visiblePreviews.length - 1];
        
        const distanceFromLeft = previewOnTheLeft ? Math.abs(previewOnTheLeft.ts - currentTimestamp) : Number.MAX_SAFE_INTEGER;
        const distanceFromRight = previewOnTheRight ? Math.abs(previewOnTheRight.ts - currentTimestamp) : Number.MAX_SAFE_INTEGER;

        if (distanceFromLeft === Number.MAX_SAFE_INTEGER && distanceFromRight === Number.MAX_SAFE_INTEGER) {
            return null;
        }

        return distanceFromLeft < distanceFromRight ? previewOnTheLeft : previewOnTheRight;
    }

    adjustPreviews = (previews) => {
        const result = [...previews];
        
        return result.sort((a, b) => b.ts - a.ts);
    }

    shouldSetRefreshPreviewsSignedUrlsTimer = (prevState) => {
        // As soon as first preview is loaded, set the timer to refresh the signed urls
        if(this.state.previews.length > 0 && prevState.previews.length === 0){
            return true;
        }

        return false;
    }

    setRefreshPreviewsSignedUrlsTimer = () => {
        this.refreshPreviewsSignedUrlsTimer = setInterval(() => {

            this.setState({
                refreshingPreviewsSignedUrls: true,

                preExistingPreviewsLoaded: false,
                eventsPreviewsLoaded: false,
                preExistingPreviewsLoading: false,
                eventsPreviewsLoading: false,
                loadingPreviewsFailed: false,
                loadingPrecisePreviewsFailed: false,
                reasonOfLoadingPreviewsFailed: undefined,
                waitingPreciseTimestamps: [],
                ignoredPreciseTimestamps: []
            });

            this.getPreviewsForTimeline();
        }, EVA_TIMELINE_REFRESH_SIGNED_URLS_DELAY);
    }

    togglePreviewsFullscreen = (e, camera) => {
        // If user clicks on the fullscreen button
        // It can be either to enter or exit fullscreen mode
        if(e.type === "click"){
            const isInFullscreen = this.previewsDivRef.current.classList.toggle("fullscreen");
        
            if(isInFullscreen){
                if(camera === "road"){
                    this.previewRoadImgRef.current.style.width = "100%";
                    this.previewCabinImgRef.current.style.width = "0";
                }
                else{
                    this.previewRoadImgRef.current.style.width = "0";
                    this.previewCabinImgRef.current.style.width = "100%";
                }
            }
            else{
                this.previewRoadImgRef.current.style.width = "50%";
                this.previewCabinImgRef.current.style.width = "50%";
            }
            
            requestEventMediaFullscreen(this.previewsDivRef);
        }
        // If user exits fullscreen mode by pressing ESC key
        // Note: I initially tried to use keydown event for capturing the ESC key press, but it didn't work as expected (Chrome does not fire keydown event for ESC key in fullscreen mode)
        //       So, I had to use fullscreenchange event to detect the exit from fullscreen mode
        //       https://stackoverflow.com/a/70131097
        else if(e.type === "fullscreenchange" && this.previewsDivRef.current.classList.contains("fullscreen")){
            this.previewsDivRef.current.classList.remove("fullscreen");
        
            this.previewRoadImgRef.current.style.width = "50%";
            this.previewCabinImgRef.current.style.width = "50%";
        }
    }

    togglePreviewCamera = () => {
        // in fullscreen mode, one preview image is set to 100% width, the other to 0% width
        // switch to the other camera preview by setting the current 100% width to 0% width, and the current 0% width to 100% width

        const currentPreviewIsRoadCamera = this.previewRoadImgRef.current.style.width === "100%" ? true : false;
        
        this.previewRoadImgRef.current.style.width = currentPreviewIsRoadCamera ? "0" : "100%";
        this.previewCabinImgRef.current.style.width = currentPreviewIsRoadCamera ? "100%" : "0";
    }

    movePreviewPopupToEvent = (newScrollLeft) => {
        // RAV-5651: If previous request for a preview have failed, and we have shown a error message
        // now that the user is interacting with the timeline again, clear the error message
        this.clearErrorMessages();

        // Hide the preview popup immediately (second parameter is force: true)
        this.togglePreviews(false, true);
        
        // timestamp of the event clicked on by the user
        let controlPanelRequestedSelectedDateUnixTs = Math.floor(this.props.controlPanelRequestedSelectedDate.getTime() / 1000);

        // First check if the preview already exists in the previews
        let eventPreview = this.requestedPreviewAlreadyExistsIn(this.state.previews, controlPanelRequestedSelectedDateUnixTs);
        
        // If the preview already exists in the previews, just update the preview popup
        if(eventPreview){
            this.updatePreviewPopup(eventPreview, true, newScrollLeft);
            return;
        }
    }

    updatePreviewPopup = (preview = null, keepHidden = false, scrollLeft) => {

        if(this.previewsDivRef.current){
            const currentPreview = preview ? preview : this.getPreviewForInterval();
            if(currentPreview){
                const cardStartUnixTs = Math.floor(this.props.cardStartDate.getTime() / 1000);
                const distanceFromTheStartOfTimeline = currentPreview.ts - cardStartUnixTs;

                // Popup is a child element of 'enhanced-video-access-panel' and not the 'eva-timeline-scroll-wrapper'
                // So to position the popup correctly, we need to adjust the left position of the popup based on the spaces between these elements
                const adjustments = EVA_TIMELINE_DRAGGER_HANDLE_WIDTH // space between the 'eva-timeline-video' and the 'eva-timeline-scroll-wrapper' : 24px
                                    + EVA_TIMELINE_CONTAINER_SIDE_MARGIN  // space between the 'enhanced-video-access-panel' and the 'eva-timeline-container' : 32px

                let timelineWrapperScrollLeft = scrollLeft != undefined ? scrollLeft : this.evaTimelineScrollWrapperContainerRef.current.scrollLeft;
                
                if(timelineWrapperScrollLeft < 0){
                    timelineWrapperScrollLeft = 0;
                }
                else{
                    const timelineWidth = this.evaVideoTimelineRef.current.getBoundingClientRect().width;
                    const timelineWrapperWidth = this.evaTimelineScrollWrapperContainerRef.current.getBoundingClientRect().width;

                    const maxScrollLeft = (timelineWidth - timelineWrapperWidth) + (2 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH);

                    if(timelineWrapperScrollLeft > maxScrollLeft){
                        timelineWrapperScrollLeft = maxScrollLeft;
                    }
                }

                const newLeft = (distanceFromTheStartOfTimeline - timelineWrapperScrollLeft) + adjustments;
                
                this.previewsDivRef.current.style.left = newLeft + "px";

                this.previewRoadImgRef.current.src = currentPreview.road ? currentPreview.road : "/images/no-preview.jpg";
                this.previewCabinImgRef.current.src = currentPreview.cabin ? currentPreview.cabin : "/images/no-preview.jpg";

                // In initial load/setup, we want to move the popup to the correct position but want to keep it hidden until the user hovers over the slider
                if(!keepHidden){
                    this.previewsDivRef.current.classList.add("visible");
                }
            }
            else{
                this.previewsDivRef.current.classList.remove("visible");
            }
        }
        
    }

    requestedPreviewAlreadyExistsIn = (listToCheckAgainst, timestamp, variance = 0) => {
        
        const list = listToCheckAgainst ? listToCheckAgainst : this.state.previews;

        for (let i = 0; i < list.length; i++) {
            const ts = list[i].ts;
            const differenceInSeconds = Math.abs((timestamp - ts));

            if (differenceInSeconds <= variance) {
                return list[i]; // Immediately return true if within range
            }
        }

        return null;
    }
    
    togglePreviews = (show, force = false) => {
        if(!this.previewsDivRef.current) return;
            
        if(show){
            if(this.hidePreviewsTimer){
                clearTimeout(this.hidePreviewsTimer);
                this.hidePreviewsTimer = undefined;
            }

            this.previewsDivRef.current.classList.add("visible");

            const events = document.querySelector('.eva-timeline-events');
            events.classList.add('show-thumbnails');
        }
        else{
            if(force){
                this.previewsDivRef.current.classList.remove("visible");

                const events = document.querySelector('.eva-timeline-events');
                events.classList.remove('show-thumbnails');

                return;
            }

            this.hidePreviewsTimer = setTimeout(() => {
                this.previewsDivRef.current.classList.remove("visible");

                const events = document.querySelector('.eva-timeline-events');
                events.classList.remove('show-thumbnails')
                
            }, EVA_TIMELINE_PREVIEWS_HIDE_DELAY);
        }
    }

    renderPreviewsLoadingStatusMessage = () => {

        // If any of the previews are still loading, show the loading spinner
        // Not the errors
        if(this.state.refreshingPreviewsSignedUrls){
            return <div className='eva-timeline-loading-thumbnails-spinner'><ProgressSpinnerSmall /><div>{strings.eva_refreshing_previews}</div></div>
        }
        else if(this.state.preExistingPreviewsLoading  || this.state.eventsPreviewsLoading || this.state.customPreviewsLoading){
            return <div className='eva-timeline-loading-thumbnails-spinner'><ProgressSpinnerSmall /><div>{strings.eva_loading_previews}</div></div>
        }
        else{
            // If the error occurred when loading the pre-existing, events or custom previews,
            if(this.state.loadingPreviewsFailed){

                // Show error message only if all previews are not loaded due to the error
                // Partial failure is okay. User still can request previews they need
                if(this.state.previews.length === 0){

                    let msg = strings.eva_failed_loading_previews;
                    // Show Retry button only if the error is not due to the preview not being available
                    const showRetryButton = this.state.reasonOfLoadingPreviewsFailed !== LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE;

                    switch(this.state.reasonOfLoadingPreviewsFailed){
                        case LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE:
                            msg = strings.eva_error_previews_not_available;
                            break;
                        case LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PREVIEWS:
                            msg = strings.eva_error_loading_previews;
                            break;
                        default:
                            break;
                    }
                    
                    return (<div className='eva-timeline-loading-thumbnails-failed'>{msg}
                                {showRetryButton && <button onClick={this.retryRequestPreviews} className='btn-refresh'><i className="material-icons">refresh</i></button>}    
                            </div>);
                }
                else{
                    if(this.state.reasonOfLoadingPreviewsFailed){
                        console.error("Error when loading the previews. Not all previews are loaded. possible reason :", this.state.reasonOfLoadingPreviewsFailed);
                    }

                    return null;
                    // return <div className='eva-timeline-loading-thumbnails-failed'>{strings.eva_failed_loading_part_of_previews}</div>
                }
            }

            // If the error occurred when loading the precise previews
            if(this.state.loadingPrecisePreviewsFailed){
                let msg = strings.eva_failed_loading_precise_previews;
                const showRetryButton = this.state.reasonOfLoadingPreviewsFailed !== LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE;
                
                switch(this.state.reasonOfLoadingPreviewsFailed){
                    case LOADING_PREVIEWS_ERROR_REASON.PREVIEW_NOT_AVAILABLE:
                        msg = strings.eva_error_precise_previews_not_available;
                        break;
                    case LOADING_PREVIEWS_ERROR_REASON.ERROR_LOADING_PREVIEWS:
                        msg = strings.eva_error_loading_precise_previews;
                        break;
                    default:
                        break;
                }
                
                return (<div className='eva-timeline-loading-thumbnails-failed'>{msg}
                            {showRetryButton && <button onClick={this.retryRequestPrecisePreviews} className='btn-refresh'><i className="material-icons">refresh</i></button>}
                        </div>);
            }

            return null;
        }
    }

    clearErrorMessages = () => {

        // If there was an error when loading precise previews, We keep the waitingPreciseTimestamps for a retry
        // But if the user has moved the mouse, we should clear the error message and the waitingPreciseTimestamps
        // Assuming they don't want a retry
        if(this.state.loadingPrecisePreviewsFailed){
            this.setState({
                waitingPreciseTimestamps: [],
            });
        }

        this.setState({
            loadingPrecisePreviewsFailed: false,
            loadingPreviewsFailed: false,
            reasonOfLoadingPreviewsFailed: undefined
        });
    }

    render() {
        
        if (!this.props.route || !this.props.events) {
            console.log("SHOW PROGRESS INDICATOR");
            return null;
        }

        // const currentPreview = this.getPreviewForInterval();

        const cardStartUnixTs = Math.floor(this.props.cardStartDate.getTime() / 1000);

        const cardEndUnixTs = Math.ceil(this.props.cardEndDate.getTime() / 1000);

        const timelineDurationSeconds = cardEndUnixTs - cardStartUnixTs;

        const timelineDemarcationsUnixTimestamps = this.minuteTimescaleDemarcationsTimestamps(cardStartUnixTs, cardEndUnixTs);
        

        // for the timeline dragger position, state is used as a fallback.  It's more efficient to apply realtime positioning updates without causing state updates.
        // however, state is still used to trigger corresponding changes in position caused by manually updating the form's start time.
        // therefore, setState for timelineDraggerLeftCoord property should be done by applying an updated this.evaVideoTimelineDraggerRefLeftCoordinate
        // (this effectively avoids state-change re-renders unless required by form updates)
        const timelineDraggerLeftCoord = this.evaVideoTimelineDraggerRefLeftCoordinate + "px";
        const timelineDraggerWidth = String(this.evaVideoTimelineDraggerRefDurationSeconds + (2 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH)) + "px";

        return (
            <>
                <div className="eva-timeline-container"
                    onMouseMove={this.props.timelineDraggerIsDragging ? this.onEnhancedVideoAccessPanelMouseMove : undefined}
                    ref={this.evaTimelineScrollWrapperContainerRef}
                >
                    <div className="eva-timeline-scroll-wrapper" style={{minWidth: String(timelineDurationSeconds/* actual duration */ + (EVA_TIMELINE_DRAGGER_HANDLE_WIDTH * 2)/* + margins on either side of timeline */ + (this.evaVideoTimelineDraggerRefDurationSeconds)) + "px"}} > {/* using min-width to ensure stretch-to-fit for very short trips, in theory */}
                        <ul className="eva-timeline-events">
                            {this.props.events.map((event, index) => {

                                const eventDate = RavenUtil.toDate(event.ts);

                                if (!eventDate) return null;

                                const eventUnixTs = Math.floor(eventDate.getTime() / 1000);

                                const eventSecondsFromCardStart = eventUnixTs + EVA_TIMELINE_DRAGGER_HANDLE_WIDTH - cardStartUnixTs;

                                return (
                                    <li className={"event-item " + event.type + " " + eventDate} style={{left: eventSecondsFromCardStart + "px"}} key={index}>
                                        <button></button>
                                    </li>)
                            })}

                            <li className='event-item-separator'>&nbsp;</li>

                            {this.state.previews.map((preview, index) => {

                                let previewSecondsFromCardStart = preview.ts - cardStartUnixTs;
                                previewSecondsFromCardStart += EVA_TIMELINE_DRAGGER_HANDLE_WIDTH; 
                                
                                const className = preview.type ? `timeline-thumbnail-label ${preview.type}` : 'timeline-thumbnail-label';
                                return (<li className={className} style={{left: previewSecondsFromCardStart + "px"}} key={index}>
                                    <div>&nbsp;</div>
                                </li>);
                            })}

                            {!this.state.loadingPrecisePreviewsFailed && this.state.waitingPreciseTimestamps.map((ts, index) => {
                                let secondsFromCardStart = ts - cardStartUnixTs;
                                secondsFromCardStart += EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
                                
                                const className = `timeline-waiting-thumbnail-label`;
                                return (<li className={className} style={{left: secondsFromCardStart + "px"}} key={index}>
                                    <div><ProgressSpinnerSmall /></div>
                                </li>);
                            })}
                        </ul>
                        <div className="eva-timeline-video"
                            ref={this.evaVideoTimelineRef}
                            onMouseDown={this.onTimelineBarClick}
                        >
                            <button
                                className="eva-timeline-dragger"
                                ref={this.evaVideoTimelineDraggerRef}
                                onMouseEnter={this.evaVideoTimelineDraggerMouseEnterHandler}
                                onMouseLeave={this.evaVideoTimelineDraggerMouseLeaveHandler}
                                style={{
                                    left: timelineDraggerLeftCoord,
                                    width: timelineDraggerWidth,
                                }}
                                onMouseDown={(event) => {
                                    this.props.setTimelineDraggerIsDragging(true)
                                }}
                            >
                                <div className="eva-dragger-background"></div>
                                <div className="eva-dragger-starttime">
                                    <i className="material-icons">drag_handle</i>
                                </div>
                                <div className="eva-dragger-endtime">
                                    <i className="material-icons">drag_handle</i>
                                </div>
                                <div className="eva-dragger-tooltip" ref={this.evaVideoTimelineDraggerTooltipRef}></div>
                            </button>
                        </div>
                        <ul className="eva-timeline-labels">
                            {timelineDemarcationsUnixTimestamps.map((markerUnixTimestamp, index) => {
                                let markerSecondsFromCardStart = markerUnixTimestamp - cardStartUnixTs;// - (EVA_TIMELINE_DEMARCATIONS_WIDTH / 2);
                                markerSecondsFromCardStart += EVA_TIMELINE_DRAGGER_HANDLE_WIDTH; // adjust for dragger handle offset
                                markerSecondsFromCardStart -= (EVA_TIMELINE_DEMARCATIONS_WIDTH / 2); // adjust for centering on a left-position
                                if (markerSecondsFromCardStart < (-1 * EVA_TIMELINE_DRAGGER_HANDLE_WIDTH)) {
                                    markerSecondsFromCardStart = 0 - EVA_TIMELINE_DRAGGER_HANDLE_WIDTH;
                                }

                                return (<li className="timeline-time-label" style={{left: markerSecondsFromCardStart + "px"}} key={index}>
                                    <div>|</div>
                                    <div>{new Date(markerUnixTimestamp * 1000).toLocaleTimeString()}</div>
                                </li>);
                            })}
                            
                        </ul>
                    </div>
                </div>
                <div className="eva-timeline-bookend-shadow left"></div>
                <div className="eva-timeline-bookend-shadow right"></div>
                <button className="eva-timeline-scroll-button left" onClick={() => {this.onScrollButtonClick(false)}}><i className="material-icons">chevron_left</i></button>
                <button className="eva-timeline-scroll-button right" onClick={() => {this.onScrollButtonClick(true)}}><i className="material-icons">chevron_right</i></button>
                <div className="eva-timeline-autoscroll-area-guide left" ref={this.evaVideoTimelineAutoScrollLeftRef}></div>
                <div className="eva-timeline-autoscroll-area-guide right" ref={this.evaVideoTimelineAutoScrollRightRef}></div>
                {this.state.previews.length > 0 && 
                    <div 
                        className='eva-timeline-thumbnail-popup'
                        onMouseEnter={this.evaVideoTimelinePreviewsPopupMouseEnterHandler}
                        onMouseLeave={this.evaVideoTimelinePreviewsPopupMouseLeaveHandler}
                        ref={this.previewsDivRef}>
                        <div className='eva-timeline-thumbnail-popup-controls road-controls'>
                            <EventMediaControl
                                id="EVA_TIMELINE_PREVIEW_TOGGLE_FULLSCREEN"
                                iconSrc={imageFullscreen}
                                render={true}
                                onClick={(e) => this.togglePreviewsFullscreen(e, 'road')}
                            />
                            <EventMediaControl
                                id="EVA_TIMELINE_PREVIEW_TOGGLE_CAMERA"
                                iconSrc={imageCamToggle}
                                render={true}
                                onClick={this.togglePreviewCamera}
                            />
                        </div>
                        <div className='eva-timeline-thumbnail-popup-controls cabin-controls'>
                            <EventMediaControl
                                id="EVA_TIMELINE_PREVIEW_TOGGLE_FULLSCREEN"
                                iconSrc={imageFullscreen}
                                render={true}
                                onClick={(e) => this.togglePreviewsFullscreen(e, 'cabin')}
                            />
                            <EventMediaControl
                                id="EVA_TIMELINE_PREVIEW_TOGGLE_CAMERA"
                                iconSrc={imageCamToggle}
                                render={true}
                                onClick={this.togglePreviewCamera}
                            />
                        </div>
                        <img ref={this.previewRoadImgRef} alt="road preview" />
                        <img ref={this.previewCabinImgRef} alt="cabin preview" />
                    </div>
                }
                {this.renderPreviewsLoadingStatusMessage()}
            </>
        );
    }
}


class RcApiQueue {
    constructor(stage, ravenId, concurrency, abortController) {
        this.queue = [];
        this.concurrency = concurrency;
        this.ravenId = ravenId;
        this.stage = stage;
        this.activeRequests = 0;
        this.apiJWT = undefined;
        this.callback = undefined;
        this.abortController = abortController;
    }

    addPostRequest(payload) {
        return this.addRequest(undefined, "POST", payload);
    }

    addGetRequest(queryStrings) {
        return this.addRequest(queryStrings, "GET");
    }

    // Add a new request to the queue
    addRequest(queryStrings, method, payload) {
        return new Promise((resolve, reject) => {
            this.queue.push({ queryStrings, method, payload, resolve, reject });
            
            this.processQueue();
        });
    }

    // Process the queue
    processQueue() {
        if (this.activeRequests >= this.concurrency) {
            return;
        }

        if (this.queue.length === 0) {
            if(this.callback) {
                this.callback();
                this.callback = undefined;
            }
            return;
        }

        const { queryStrings, method, payload, resolve, reject } = this.queue.shift();
        this.activeRequests++;

        this.makeRequest(queryStrings, method, payload)
            .then(response => {
                this.activeRequests--;
                if(resolve) resolve(response);
                this.processQueue();
            })
            .catch(error => {
                
                if (error.name === 'AbortError') {
                    this.handleAbort();
                }

                this.activeRequests--;
                if(reject) reject(error);
                this.processQueue();
            });
    }

    handleAbort() {
        console.warn("Aborted loading previews. clearing queue", this.queue, this.callback);
        this.queue = [];
        this.callback = undefined;
    }

    async makeRequest(queryStrings, method, payload) {

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        const url = queryStrings ? `${apiBaseUrl}/ravens/${this.ravenId}/media?${queryStrings}` : `${apiBaseUrl}/ravens/${this.ravenId}/media`;
        
        return RcAPI.apiRequest(url, method, payload, this.stage, this.abortController.signal)
        .catch((error) => {
            throw error;
        });
    }
}
